import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import eodhd from '../services/eodhd';
import { JSE_STOCKS } from '../utils/constants';

/**
 * Prefetches JSE all-quotes data into the React Query cache on mount.
 *
 * JSE is the default active tab on the Markets page and can never prefetch
 * itself (it is always the first live query). By prefetching from Layout,
 * which wraps every authenticated page, the cache is warm before the user
 * navigates to Markets — regardless of which page they came from.
 *
 * Uses the same cache key, staleTime, gcTime, and transform as Markets.tsx
 * buildQueryFn('jse') so the cached shape matches what Markets reads.
 *
 * prefetchQuery is idempotent: if the cache entry is still within staleTime
 * (15 min) it is a no-op and no network request is made.
 */
export function useJSEPrefetch(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: ['market-data', 'jse'] as const,
      queryFn: async () => {
        let jseQuotes;
        try {
          jseQuotes = await eodhd.getAllJSEQuotes();
        } catch (err) {
          console.warn('[useJSEPrefetch] Failed to prefetch JSE quotes:', err);
          throw err; // Don't cache the failure — let Markets.tsx fetch fresh
        }
        return JSE_STOCKS.map((asset) => {
          const quote = jseQuotes.find(q => q.symbol === asset.symbol || q.code === asset.symbol);
          if (quote) {
            return {
              symbol: asset.symbol,
              name: asset.name,
              price: quote.close || 0,
              change: quote.change_percent ?? quote.change_p ?? 0,
              volume: quote.volume?.toLocaleString() || 'N/A',
              previousClose: quote.previousClose ?? quote.previous_close,
            };
          }
          return { symbol: asset.symbol, name: asset.name, price: null, change: 0, volume: 'N/A', previousClose: undefined };
        });
      },
      staleTime: 15 * 60 * 1000,
      gcTime: 20 * 60 * 1000,
    });
  }, [queryClient]);
}
